import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { Form, Col, Row } from "react-bootstrap";

// hooks
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { useHistory, useLocation } from "react-router-dom";
import { format } from "date-fns";

// components
import Sallie from "../UIComponents/Sallie";
import LabelledInput from "../UIComponents/LabelledInput";
import DescribeStrokeSchema from "./validations/describeStrokeSchema";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import { createStroke } from "../../api/TBIRequests";
import customizeMessages from "../../lib/customizeMessages";

const RecentStroke1 = ({ user, setOnboardingPercent }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(DescribeStrokeSchema),
  });

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(30)
      : setOnboardingPercent(20);
    setLoading(false);
  }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      data.stroke_incident_date = format(
        new Date(data.stroke_incident_date),
        "MM-dd-yyyy"
      );
      await createStroke({ patientId: patient.patientId, data });
      setSubmitting(false);
      history.push("/RecentStroke2", {
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      console.log(err);
      history.push("/oops");
    }
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("recentstroke1.header.patient"),
      caregiver: t("recentstroke1.header.caregiver"),
    },
    sallieText: {
      Stroke: t("recentstroke1.sallie.patient"),
      caregiver: t("recentstroke1.sallie.caregiver"),
    },
    dateOfTbiLabel: {
      Stroke: t("recentstroke1.date.patient"),
      caregiver: t("recentstroke1.date.caregiver"),
    },
    addTbis: {
      tbiPatient: "You will have the option to add other TBI's after you set up your account.",
      caregiver: "You will have the option to add other TBI's after you set up your account.",
    },
    describeEventLabel: {
      Stroke: t("recentstroke1.describe.label.patient"),
      caregiver: t("recentstroke1.describe.label.caregiver"),
    },
    describeEventExtraInfo: {
      Stroke: t("recentstroke1.describe.info"),
      caregiver: t("recentstroke1.describe.info"),
    },
    describeEventPlaceholder: {
      Stroke: "",
      caregiver: "",
    },
    totalTbiLabel: {
      Stroke: t("recentstroke1.number.patient"),
      caregiver: t("recentstroke1.number.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <div className="d-block">
            <OnboardingStepHeader text={getMessage("mainHeader")} />
            <Sallie text={getMessage("sallieText")} />
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-block">
              <Form.Row>
                <Col>
                  <LabelledInput
                    autoFocus
                    required
                    labelAs="h6"
                    name="stroke_incident_date"
                    placeholder="MM/DD/YYYY"
                    type="date"
                    date-format="mm/dd/yyyy"
                    inputRef={register}
                    label={getMessage("dateOfTbiLabel")}
                    errors={errors}
                  />
                </Col>
                <Col className="d-none align-items-end">
                  <div className="mb-3 ml-3">{getMessage("addTbis")}</div>
                </Col>
              </Form.Row>
              <LabelledInput
                inputAs="textarea"
                required
                labelAs="h6"
                extraInfo={getMessage("describeEventExtraInfo")}
                name="describeEvent"
                placeholder={getMessage("describeEventPlaceholder")}
                type="text"
                inputRef={register}
                label={getMessage("describeEventLabel")}
                errors={errors}
                rows={3}
              />
              <LabelledInput
                name="total_stroke"
                label={getMessage("totalTbiLabel")}
                labelAs="h6"
                required
                type="number"
                inputRef={register}
                errors={errors}
                min={1}
              />
              <Row>
                <SubmitButton type="submit" disabled={submitting}>
                  {t("onboarding.saveContinue")} &gt;
                </SubmitButton>
              </Row>
            </div>
          </Form>
        </>
      )}
    </>
  );
};

export default RecentStroke1;